import * as React from 'react';
import { useAppSelector } from 'Store/index';
import { getDiseaseModifyingMedications } from '../utils';
import { isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { useIntl } from 'react-intl';
import { intersperse } from 'ramda';
import { sortDocuments } from '../../../utility/randomUtil';
import MedicationTile from 'Components/DashboardTile/TileContentMaker/components/MedicationTile';
import { getAtcCodesByPlatform } from '../Document/config';
import { filterMedicationsOverlapping } from 'Store/documents/helpers/documentFilters';

// Return latest SMA/DMD medication sorted its administration dates
const findLatestSmaDmdMedication = (
  medicationSettings: IOrganizationNeuroSettings['medicationSettings'] | undefined,
  platform: string,
  documents: IMedication[],
): IMedication[] => {
  /** Medications visualized in tile are different from graph, so define them here */
  const atcCodesToVisualize = getAtcCodesByPlatform(medicationSettings, platform, { categories: ['tile'] });

  const medications = (documents ?? []).filter(document => {
    return atcCodesToVisualize.includes(document.atc ?? '');
  });

  return medications;
};

const GeneralTile = ({ documents }: { documents: Array<IMedication>; platform: Platform }): React.JSX.Element => {
  const platformMedications: IMedication[] =
    documents && documents.length > 0 ? documents.filter(d => d.hasEnded?.[0] !== true) : [];
  const platformMedicationsEnded: IMedication[] =
    documents && documents.length > 0 ? documents.filter(d => d.hasEnded?.[0] === true) : [];

  const latestMedication: IMedication | undefined = platformMedications.sort((a, b) =>
    sortDocuments([{ type: 'date', sortField: 'startDate' }])(a, b),
  )[0];
  const latestMedicationEnded: IMedication | undefined = platformMedicationsEnded.sort((a, b) =>
    sortDocuments([{ type: 'date', sortField: 'endDate' }])(a, b),
  )[0];

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const dataPicker = (document: IMedication) => {
    const dateFieldName = isPartialDate(document?.usageStartDate) ? 'usageStartDate' : 'startDate';
    const dateValue = document?.[dateFieldName];
    return {
      title: fm(document.hasEnded?.[0] === true ? 'medication.tileEnded' : 'medication.tileStarted'),
      medicationName: document.medicationName,
      date: isPartialDate(dateValue) ? formatPartialDate(dateValue) : null,
      endDate:
        isPartialDate(document.endDate) && document.hasEnded?.[0] === true ? formatPartialDate(document.endDate) : null,
      endReasons: intersperse(
        ', ',
        (document.endReason || []).map(reason => fm('medication.opts.' + reason)),
      ),
      key: document._id,
    };
  };

  return (
    <React.Fragment>
      {latestMedication || latestMedicationEnded ? (
        <MedicationTile
          data={[
            ...(latestMedication ? [dataPicker(latestMedication)] : []),
            ...(latestMedicationEnded ? [dataPicker(latestMedicationEnded)] : []),
          ]}
        />
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

const MedicationDash = ({ documents: documentsRedux }: IOwnProps): React.JSX.Element | null => {
  const clonedDocs = structuredClone(documentsRedux) || [];

  const orgSettings = useAppSelector(s => s.settings.orgSettings);

  const documents: IMedication[] = orgSettings.medicationHideOverlapping
    ? filterMedicationsOverlapping(clonedDocs, orgSettings['medicationSettings'])
    : clonedDocs;

  // Only use documents owned by current organization
  const orgId = useAppSelector((s: IState) => s.session.data?.orgid);
  const selectedMedicationOrg = useAppSelector((s: IState) => s.session.selectedMedicationOrg) || orgId;
  const thisOrgDocuments = documents.filter(
    d =>
      d._ownerOrg === selectedMedicationOrg ||
      (d._pastOwners && Object.keys(d._pastOwners).some(org => org === selectedMedicationOrg)),
  );
  const platform = useAppSelector(s => s.session.platforms?.selected);

  const studyMedications = thisOrgDocuments.filter(d => d.isClinicalStudy);

  const medicationSettings = useAppSelector((s: IState) => s.settings?.orgSettings?.medicationSettings);

  return thisOrgDocuments && platform ? (
    <React.Fragment>
      <GeneralTile
        documents={
          platform === 'sma' || platform === 'dmd'
            ? [...findLatestSmaDmdMedication(medicationSettings, platform, thisOrgDocuments || []), ...studyMedications]
            : [
                ...getDiseaseModifyingMedications(medicationSettings, thisOrgDocuments || [], platform),
                ...studyMedications,
              ]
        }
        platform={platform}
      />
    </React.Fragment>
  ) : null;
};

interface IOwnProps {
  documents?: IMedication[];
}

export default MedicationDash;
